<script setup lang="ts">
import { computed } from 'vue';
import { RouteLocationRaw } from 'vue-router';
import { DgrIcon, DgrPopover } from '@stockmarkteam/donguri-ui';
import NotificationMark from '@/components/notifications/notification-mark.vue';
import { useUserDocumentNotification } from '@/utils/composables/useUserDocumentNotification';
import { featureFlags } from '@/featureFlags';

/**
 * 専用の設定画面を持つエージェント（情報収集・技術探索・特許調査）だけラベルを渡す。
 * 渡されないエージェントではAconnectの設定だけを出す。
 */
defineProps<{
  agentSettingsLabel?: string;
  agentSettingsDescription?: string;
  agentSettingsRoute?: RouteLocationRaw;
}>();

// Flag が無効のときはメニューを出さないので、管理者向けの API も呼ばない
const { hasUserDocumentNotification } = featureFlags.ANDEV_10131_CAPABILITY
  ? useUserDocumentNotification()
  : { hasUserDocumentNotification: computed(() => false) };

const dropdownZIndex = Number(
  // ヘッダーよりも上に表示する
  getComputedStyle(document.body).getPropertyValue('--z-header') + 1,
);
</script>

<template>
  <DgrPopover
    v-if="featureFlags.ANDEV_10131_CAPABILITY"
    :z-index="dropdownZIndex"
  >
    <template #default="{ triggerProps }">
      <button
        v-bind="triggerProps"
        v-tooltip="{ content: '設定', triggers: ['hover'] }"
        type="button"
        class="settings-menu"
      >
        <DgrIcon name="cog" class="icon" />
        <NotificationMark
          v-if="hasUserDocumentNotification"
          top="1px"
          right="0"
        />
      </button>
    </template>
    <template #content="{ close }">
      <div class="settings-dropdown">
        <template v-if="agentSettingsLabel">
          <component
            :is="agentSettingsRoute ? 'router-link' : 'div'"
            :to="agentSettingsRoute"
            class="dropdown-item"
            :class="{ 'dropdown-link': agentSettingsRoute }"
            @click="agentSettingsRoute && close()"
          >
            <div class="item-title c-text c-text--m">
              {{ agentSettingsLabel }}
            </div>
            <div
              v-if="agentSettingsDescription"
              class="item-description c-text c-text--xs"
            >
              {{ agentSettingsDescription }}
            </div>
          </component>
          <hr class="dropdown-divider" />
        </template>
        <router-link
          :to="{ name: 'settingsOrganizationTags' }"
          class="dropdown-item dropdown-link external-item"
          target="_blank"
          @click="close"
        >
          <div>
            <div class="item-title c-text c-text--m">
              Aconnectの管理・設定
              <span
                v-if="hasUserDocumentNotification"
                class="notification-dot"
              />
            </div>
            <div class="item-description c-text c-text--xs">
              メンバー管理やIPアドレスなど、組織全体の設定を管理します。
            </div>
          </div>
          <DgrIcon
            name="external-link"
            size="small"
            :keep-fill="false"
            class="external-icon"
          />
        </router-link>
      </div>
    </template>
  </DgrPopover>
</template>

<style scoped lang="scss">
.settings-menu {
  &:hover {
    background: $color-gray200;
  }

  position: relative;

  border: none;
  background: none;
  width: 32px;
  height: 32px;
  padding: 4px;
}

.settings-dropdown {
  box-shadow: 0px 1px 5px rgba($color-gray1000, 0.25);
  border-radius: 4px;
  padding: 4px;

  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 340px;

  .dropdown-item {
    display: block;
    padding: 4px 8px;
    border-radius: 2px;
    cursor: default;
  }

  .dropdown-link {
    cursor: pointer;

    &:hover {
      background: $color-gray200;
    }

    .item-title {
      color: $color-gray1000;
    }
  }

  .item-title {
    display: flex;
    align-items: center;
    gap: 4px;
    color: $color-gray600;
  }

  .notification-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    margin-left: 4px;
    background: $color-orange1000;
  }

  .item-description {
    color: $color-gray800;
  }

  .external-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }

  .external-icon {
    flex-shrink: 0;
    color: $color-gray1000;
  }

  .dropdown-divider {
    margin: 0;
    border-top: 1px solid $color-gray400;
    border-bottom: 0;
  }
}

.icon {
  width: 24px;
  height: 24px;
}
</style>
